import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, PanelLeftOpen, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useElementWidth } from "@/hooks/use-element-width";
import { Skeleton } from "@/components/common";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ProjectChat } from "@/components/canvas/ProjectChat";
import { CaseUploads } from "@/components/intake/CaseUploads";
import { ReadingStatus } from "@/components/canvas/ReadingStatus";
import { ActivityPanel } from "@/components/canvas/ActivityPanel";
import { InspectorSlot } from "@/components/canvas/Inspector";
import { DocViewer } from "@/components/canvas/DocViewer";
import { RisikoSheet } from "@/components/canvas/risiko/RisikoSheet";
import { KapasitetSheet } from "@/components/canvas/kapasitet/KapasitetSheet";
import { ResizeHandle } from "@/components/layout/ResizeHandle";
import { Segmented } from "@/components/layout/Segmented";
import { useCaseUploads } from "@/hooks/useCaseUploads";
import { SplitDialog } from "@/components/canvas/underjobber/SplitDialog";
import { MengderSheet } from "@/components/canvas/MengderSheet";
import { filesFromDrop } from "@/lib/dropFiles";
import { OversiktTab } from "@/components/canvas/OversiktTab";
import { KravTab } from "@/components/canvas/KravTab";
import { KalkyleTab } from "@/components/canvas/KalkyleTab";
import { TilbudTab } from "@/components/canvas/TilbudTab";
import { MeldingerTab } from "@/components/meldinger/MeldingerTab";
import { ShareButton, ProjectMenu } from "@/components/canvas/CanvasMenu";
import { TABS, phaseLabel, isDraftCase } from "@/lib/sk";
import { PANELS, RAIL_W, MIN_WORK_W, readPref, writePref, readWidth, writeWidth, clampW } from "@/lib/layout";
import { useResource, invalidate } from "@/hooks/use-resource";

const CHAT_KEY = "sk.canvas.chat.hidden"; // "1" | "0" | null – until Egil has chosen, the assistant yields to established work
const MOBILE_VIEW_KEY = "sk.canvas.mobile.view";
const CANVAS_W = { oversikt: "max-w-[920px]", krav: "max-w-[1040px]", kalkyle: "", tilbud: "flex min-h-full flex-col", meldinger: "h-full" };
const PLACEHOLDER = "Spør eller be om en endring …";
const perUser = (key, user) => `${key}.${user?.id || "anon"}`;

/**
 * Oversikt · Arbeidsomfang · Kalkyle · Tilbud │ Meldinger – one floating, centred control: a near-transparent track, the
 * active tab a white surface that glides. No underline, no colour. One thin divider before Meldinger (a place to talk, not
 * a production step). On narrow screens the row scrolls sideways instead of shrinking the type.
 */
function TabBar({ tab, setTab, mobile, mobileView, setMobileView, unread = 0 }) {
  const source = mobile ? [{ key: "chat", label: "Assistent", short: "Assistent", group: "chat" }, ...TABS] : TABS;
  const current = mobile && mobileView === "chat" ? "chat" : tab;
  const pick = (key) => {
    if (key === "chat") return setMobileView("chat");
    setTab(key);
    if (mobile) setMobileView("canvas");
  };
  const items = source.map((t2) => ({
    key: t2.key,
    divider: t2.key === "meldinger",
    testId: t2.key === "chat" ? "project-view-chat" : `canvas-tab-${t2.key}`,
    label: <>{mobile ? t2.short : t2.label}{t2.key === "meldinger" && unread ? <span className="tabular rounded-full bg-primary px-1.5 text-[11px] font-semibold leading-[16px] text-white" data-testid="canvas-tab-meldinger-unread">{unread}</span> : null}</>,
  }));
  return <Segmented value={current} onChange={pick} items={items} size="md" layoutId={mobile ? "canvas-tab-active-m" : "canvas-tab-active"} ariaLabel="Arbeidsflate" testId="canvas-tabs" scroll={mobile} className={mobile ? "mx-2" : undefined} />;
}

/** The assistant lives in the left context panel while it is open – «‹ Arbeid» brings the case list back. */
function AssistantHead({ onClose }) {
  return (
    <div className="flex h-[56px] shrink-0 items-center justify-between pl-2 pr-2" data-testid="assistant-head">
      <button type="button" onClick={onClose} className="inline-flex h-8 items-center gap-1 rounded-[9px] px-2 text-[13px] font-medium text-foreground/60 outline-none transition-colors hover:bg-black/[0.05] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40" data-testid="assistant-back"><ChevronLeft className="h-4 w-4" /> Arbeid</button>
      <div className="inline-flex items-center gap-1.5 font-display text-[15px] font-semibold tracking-[-0.01em]"><Sparkles className="h-3.5 w-3.5 text-primary" strokeWidth={2} /> Assistent</div>
      <button type="button" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-[9px] text-foreground/55 outline-none transition-colors hover:bg-black/[0.05] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40" aria-label="Skjul assistenten" data-testid="assistant-close"><X className="h-4 w-4" /></button>
    </div>
  );
}

/** A link that no longer leads anywhere (deleted or replaced case) – say so plainly and point back to the work. */
function CaseError({ missing, msg }) {
  return (
    <div className="flex h-full items-center justify-center bg-background p-8" data-testid="project-error" data-missing={missing ? "1" : "0"}>
      <div className="max-w-[380px] text-center">
        <div className="font-display text-[20px] font-semibold tracking-[-0.02em]" data-testid="project-error-title">{missing ? "Saken finnes ikke lenger" : "Kunne ikke åpne saken"}</div>
        <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground" data-testid="project-error-text">{missing ? "Lenken går til en sak som er slettet eller erstattet. Listen er oppdatert – finn saken igjen i Arbeid." : msg}</p>
        <div className="mt-5 flex items-center justify-center gap-2">
          {missing ? <Link to="/arbeid" className="inline-flex h-10 items-center rounded-full bg-[hsl(var(--ink))] px-4 text-[13px] font-medium text-white transition-colors hover:bg-[hsl(var(--ink-2))]" data-testid="project-error-work-link">Gå til Arbeid</Link>
            : <button type="button" onClick={() => window.location.reload()} className="inline-flex h-10 items-center rounded-full bg-[hsl(var(--ink))] px-4 text-[13px] font-medium text-white transition-colors hover:bg-[hsl(var(--ink-2))]" data-testid="project-error-retry">Prøv igjen</button>}
          <Link to="/" className="inline-flex h-10 items-center rounded-full px-4 text-[13px] font-medium text-foreground/70 transition-colors hover:bg-black/[0.05] hover:text-foreground" data-testid="project-error-back-link">Til start</Link>
        </div>
      </div>
    </div>
  );
}


/**
 * The case canvas: one persistent working document per request. Navigation left (Arbeid – or the assistant when it is
 * open), the work in the middle, details in the right inspector. Oversikt → Arbeidsomfang → Kalkyle → Tilbud. Only inner regions scroll.
 */
export default function ProjectPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { panelCollapsed, togglePanel, leftSlot, leftPanelWidth, leftLocked, requestAutoHide, railWidth } = useOutletContext() || {};
  const setLeftActive = leftSlot?.setActive;
  const isLg = useMediaQuery("(min-width: 1024px)");
  const sm = useMediaQuery("(min-width: 640px)");
  const [project, setProject] = useState(null);
  const [ctx, setCtx] = useState(null);
  const ctxRef = useRef(null);
  ctxRef.current = ctx;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("oversikt");
  const [threadId, setThreadId] = useState(() => new URLSearchParams(window.location.search).get("thread"));
  const [compose, setCompose] = useState(null); // {item_ids, doc_id} – «Start samtale» from a post lands here
  const threadsUrl = `/projects/${id}/threads`;
  const { data: threads } = useResource(threadsUrl, null, { interval: 30000 });
  const [filter, setFilter] = useState(null);
  const [focus, setFocus] = useState(null);
  const [intent, setIntent] = useState(null);
  const [mobileView, setMobileViewState] = useState(() => localStorage.getItem(perUser(MOBILE_VIEW_KEY, user)) || "chat");
  const setMobileView = useCallback((v) => { localStorage.setItem(perUser(MOBILE_VIEW_KEY, user), v); setMobileViewState(v); }, [user]);
  const [chatPref, setChatPref] = useState(() => localStorage.getItem(perUser(CHAT_KEY, user)));
  const [unseen, setUnseen] = useState(0);
  const [target, setTarget] = useState(null);
  const [panel, setPanel] = useState(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [slotEl, setSlotEl] = useState(null);
  const [inspOpen, setInspOpen] = useState(false);
  const [inspWidth, setInspWidth] = useState(() => readWidth(PANELS.inspector, user));
  const [wide, setWideState] = useState(() => readPref("inspector.wide", user) === "1");
  const setWide = useCallback((v) => { setWideState(v); writePref("inspector.wide", user, v ? "1" : "0"); }, [user]);
  const resizeInsp = useCallback((w) => { const v = clampW(PANELS.inspector, w); setInspWidth(v); writeWidth(PANELS.inspector, user, v); }, [user]);
  const resetInsp = useCallback(() => { setInspWidth(PANELS.inspector.def); writeWidth(PANELS.inspector, user, null); setWide(false); }, [user, setWide]);
  const canvasRef = useRef(null);
  const canvasW = useElementWidth(canvasRef);
  const targetRef = useRef(null);
  targetRef.current = target;
  const [winW, setWinW] = useState(window.innerWidth);
  useEffect(() => {
    const onWin = () => setWinW(window.innerWidth);
    window.addEventListener("resize", onWin);
    return () => window.removeEventListener("resize", onWin);
  }, []);
  const tabRef = useRef(tab);
  tabRef.current = tab;
  // What the user sees (tab, view, filter, open groups, rows) – the tabs report it; the conversation sends it with every message.
  const screenRef = useRef(null);
  const reportScreen = useCallback((s) => { screenRef.current = s ? { tab: tabRef.current, ...s } : null; }, []);
  useEffect(() => { if (screenRef.current && screenRef.current.tab !== tab) screenRef.current = null; }, [tab]);
  useEffect(() => {
    // «Gå til post» / «Se avklaringer» from Oversikt, Tilbud or the conversation: switch view, focus the item, set the filter.
    const go = (e) => {
      const d = e.detail || {};
      if (d.tab) setTab(d.tab);
      if (d.thread) setThreadId(d.thread);
      if (d.compose) { setThreadId(null); setCompose({ ...d.compose, at: Date.now() }); }
      if (d.ref) setFocus(d.ref);
      if ("filter" in d) setFilter(d.filter || null);
      if (d.panel) setPanel(d.panel);
      if (d.add || d.kontroll || d.risiko || d.revision || d.diff || d.revisjon || d.send) setIntent({ add: Boolean(d.add), kontroll: Boolean(d.kontroll), risiko: Boolean(d.risiko), send: Boolean(d.send), revision: d.revision || null, diff: d.diff || null, revisjon: d.revisjon || null, at: Date.now() });
      if (!isLg) setMobileView("canvas");
    };
    window.addEventListener("canvas:goto", go);
    return () => window.removeEventListener("canvas:goto", go);
  }, [isLg, setMobileView]);

  useEffect(() => {
    setError(null);
    // Read the way in now, not when the answer arrives – a second run of this effect must not see the already-cleaned URL.
    const sp = new URLSearchParams(window.location.search);
    const wanted = sp.get("tab");
    api.get(`/projects/${id}`).then((r) => {
      setProject(r.data.project);
      setCtx(r.data.context);
      const last = r.data.project.last_tab;
      const pick = wanted && TABS.some((t) => t.key === wanted) ? wanted : last && TABS.some((t) => t.key === last) ? last : "oversikt";
      setTab(pick);
      // «?kontroll=1» / «?send=1» from Hjem or Arbeid: land directly on the control surface or the send dialog.
      if (sp.get("kontroll") === "1" || sp.get("send") === "1") setIntent({ kontroll: sp.get("kontroll") === "1", send: sp.get("send") === "1", at: Date.now() });
      if ((wanted || r.data.project.status === "utkast") && !isLg) setMobileView("canvas");
      // «?tab=» is a way in, not a state: once used, the tab lives in last_tab – a refresh keeps the tab you moved to.
      if (wanted) { sp.delete("tab"); navigate({ search: sp.toString() ? `?${sp}` : "" }, { replace: true }); if (pick !== last) api.patch(`/projects/${id}`, { last_tab: pick }).catch(() => {}); }
    }).catch((e) => {
      const missing = e?.response?.status === 404;
      setError({ msg: errMsg(e), missing });
      // A link to a case that no longer exists usually comes from a stale list – refresh every list so it disappears.
      if (missing) window.dispatchEvent(new Event("projects:changed"));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const apply = useCallback((r) => {
    if (r.data.project) setProject(r.data.project);
    if (r.data.context) setCtx(r.data.context);
    const ui = r.data.ui || r.data.message?.ui;
    if (ui?.tab) setTab(ui.tab);
    if (ui?.filter) setFilter(ui.filter === "all" ? "alle" : ui.filter);
    if (ui?.focus_ref) setFocus(ui.focus_ref);
    if (ui?.tab && !isLg) setMobileView("canvas");
    window.dispatchEvent(new Event("projects:changed"));
  }, [isLg, setMobileView]);

  const send = useCallback(async (content) => {
    if (!content?.trim() || busy) return;
    const optimistic = { id: `tmp-${Date.now()}`, role: "user", content, created_at: new Date().toISOString() };
    setProject((p) => (p ? { ...p, messages: [...(p.messages || []), optimistic] } : p));
    setBusy(true);
    setMobileView("chat");
    try {
      apply(await api.post(`/projects/${id}/message`, { content, origin: window.location.origin, tab: tabRef.current, focus: targetRef.current?.ref || "", screen: screenRef.current && screenRef.current.tab === tabRef.current ? screenRef.current : null }));
    } catch (e) {
      toast.error(errMsg(e));
      setProject((p) => (p ? { ...p, messages: (p.messages || []).filter((m) => m.id !== optimistic.id) } : p));
    } finally {
      setBusy(false);
    }
  }, [id, busy, apply, setMobileView]);

  const act = useCallback(async (type, params) => {
    if (busy) return;
    setBusy(true);
    try {
      apply(await api.post(`/projects/${id}/action`, { type, params: params || {}, origin: window.location.origin, tab: tabRef.current }));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  }, [id, busy, apply]);

  /** Threads: the human dialogue on the case – one model behind Meldinger, the item comment and the sidebar. Never a KI turn, never an applied change. */
  const threadOps = useMemo(() => {
    const done = () => { invalidate(threadsUrl); invalidate("/threads/summary"); };
    const wrap = async (p) => { try { const r = await p; done(); return r.data; } catch (e) { toast.error(errMsg(e)); throw e; } };
    return {
      create: (body) => wrap(api.post(threadsUrl, body)),
      reply: (tid, body, kind) => wrap(api.post(`${threadsUrl}/${tid}/messages`, typeof body === "string" ? { content: body, kind: kind || "melding" } : { kind: "melding", ...body })),
      patch: (tid, body) => wrap(api.patch(`${threadsUrl}/${tid}`, body)),
      react: (tid, mid, emoji) => wrap(api.post(`${threadsUrl}/${tid}/messages/${mid}/reactions`, { emoji })),
      upload: async (file) => { const fd = new FormData(); fd.append("file", file, file.name); try { const r = await api.post(`${threadsUrl}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } }); return r.data; } catch (e) { toast.error(errMsg(e)); throw e; } },
      open: (tid) => window.dispatchEvent(new CustomEvent("canvas:goto", { detail: { tab: "meldinger", thread: tid } })),
      start: (preset) => window.dispatchEvent(new CustomEvent("canvas:goto", { detail: { tab: "meldinger", compose: preset || {} } })),
    };
  }, [threadsUrl]);

  const up = useCaseUploads(id, apply);
  const upload = useCallback((files) => { if (!busy && !up.busy) up.add(files); }, [busy, up]);
  // Documents can be dropped anywhere on the work surface – not only in the assistant. Files only; drags inside the app (our own MIME) pass through.
  const [dropping, setDropping] = useState(false);
  const dropDepth = useRef(0);
  const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  const onCanvasDragEnter = (e) => { if (!isFileDrag(e)) return; e.preventDefault(); dropDepth.current += 1; setDropping(true); };
  const onCanvasDragOver = (e) => { if (!isFileDrag(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = "copy"; };
  const onCanvasDragLeave = (e) => { if (!isFileDrag(e)) return; dropDepth.current = Math.max(0, dropDepth.current - 1); if (!dropDepth.current) setDropping(false); };
  const onCanvasDrop = (e) => { if (!isFileDrag(e)) return; e.preventDefault(); dropDepth.current = 0; setDropping(false); filesFromDrop(e.dataTransfer).then((files) => { if (files.length) upload(files); }); };

  /** Background KI reading: poll calmly while it runs, so facts and the agent's follow-up arrive without a reload. */
  const readingStatus = project?.reading?.status;
  const ocrRunning = (ctx?.documents || []).some((d) => ["pending", "running"].includes(d.ocr?.status));
  const kiRunning = project?.ki_utkast?.status === "running";
  useEffect(() => {
    if (readingStatus !== "running" && !ocrRunning && !kiRunning) return undefined;
    const t = setInterval(() => api.get(`/projects/${id}`).then(apply).catch(() => {}), 2500);
    return () => clearInterval(t);
  }, [readingStatus, ocrRunning, kiRunning, id, apply]);
  const readingCtl = useCallback(async (action) => {
    try {
      const r = await api.post(`/projects/${id}/reading`, { action });
      setProject((p) => (p ? { ...p, reading: r.data.reading } : p));
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [id]);
  const dismissReading = useCallback(() => setProject((p) => (p ? { ...p, reading: null } : p)), []);

  /** Approve / reject an AI proposal. The server is idempotent and refuses stale previews – we just show what it says. */
  const decide = useCallback(async (pid, decision) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/projects/${id}/proposals/${pid}/${decision}`, {});
      apply(r);
      if (decision === "approve") {
        const changeId = r.data.proposal?.result?.change_id;
        if (r.data.already) toast.success("Forslaget var allerede utført");
        else toast.success("Utført – lagret i kalkylen", changeId ? { action: { label: "Angre", onClick: () => api.post(`/projects/${id}/undo`, { change_id: changeId }).then((u) => { apply(u); toast.success(`Angret: ${u.data.undone?.label || "forslaget"}`); }).catch((e) => toast.error(errMsg(e))) } } : undefined);
      } else toast("Forslaget er avvist – ingenting ble endret");
    } catch (e) {
      toast.error(errMsg(e));
      try { apply(await api.get(`/projects/${id}`)); } catch { /* keep current view */ }
    } finally {
      setBusy(false);
    }
  }, [id, busy, apply]);

  const feedback = useCallback(async (m, value) => {
    setProject((p) => (p ? { ...p, messages: (p.messages || []).map((x) => (x.id === m.id ? { ...x, feedback: value } : x)) } : p));
    try {
      await api.post(`/projects/${id}/feedback`, { message_id: m.id, value });
      if (value) toast.success(value === "up" ? "Takk – godt å høre" : "Takk – det hjelper oss å bli bedre");
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [id]);

  /** Every direct edit in the document goes through here: the server recalculates and returns the fresh context. */
  const run = useCallback(async (promise, okMsg) => {
    try {
      const r = await promise;
      apply(r);
      if (okMsg) toast.success(okMsg);
      return r.data;
    } catch (e) {
      toast.error(errMsg(e));
      throw e;
    }
  }, [apply]);

  // The assistant is a tool beside the work: open while a case is being read and set up, one click away once it is established.
  const established = Boolean(project && !isDraftCase(project) && project.reading?.status !== "running" && (ctx?.items?.length || 0) > 0);
  const chatHidden = chatPref != null ? chatPref === "1" : established;
  const setChatHidden = useCallback((hidden) => {
    const next = hidden ? "1" : "0";
    localStorage.setItem(perUser(CHAT_KEY, user), next);
    setChatPref(next);
  }, [user]);
  const toggleChat = useCallback(() => setChatHidden(!chatHidden), [chatHidden, setChatHidden]);

  const ops = useMemo(() => ({
    act,
    thread: threadOps,
    apply: (data) => apply({ data }),
    patchItem: (itemId, field, value) => run(api.patch(`/projects/${id}/items/${itemId}`, { field, value })),
    addItem: (body) => run(api.post(`/projects/${id}/items`, body), "Post lagt til"),
    deleteItem: (itemId) => run(api.delete(`/projects/${id}/items/${itemId}`), "Posten er fjernet"),
    addElement: (itemId, body) => run(api.post(`/projects/${id}/items/${itemId}/elements`, body)),
    patchElement: (elId, field, value) => run(api.patch(`/projects/${id}/elements/${elId}`, { field, value })),
    deleteElement: (elId) => run(api.delete(`/projects/${id}/elements/${elId}`)),
    addNote: (itemId, coll, body) => run(api.post(`/projects/${id}/items/${itemId}/${coll}`, body)),
    patchEntry: (itemId, coll, entryId, body) => run(api.patch(`/projects/${id}/items/${itemId}/${coll}/${entryId}`, body)),
    deleteEntry: (itemId, coll, entryId) => run(api.delete(`/projects/${id}/items/${itemId}/${coll}/${entryId}`)),
    patchDoc: (docId, body) => run(api.patch(`/projects/${id}/documents/${docId}`, body)),
    deleteDoc: (docId) => run(api.delete(`/projects/${id}/documents/${docId}`), "Dokumentet er fjernet fra saken"),
    patchCase: (body, msg) => run(api.patch(`/projects/${id}`, body), msg),
    restructure: async (reparse) => {
      const count = (c) => (c?.items || []).filter((i) => i.kind === "customer").length;
      const before = count(ctxRef.current);
      try {
        const r = await api.post(`/projects/${id}/restructure${reparse ? "?reparse=1" : ""}`);
        apply(r);
        const st = r.data?.stats || {};
        toast.success(st.added ? `${st.added} arbeidsposter ${st.from_text ? "lest ut av kundens tekst" : "lest fra dokumentene"}` : "Arbeidspostene er gjennomgått på nytt");
        return r.data;
      } catch (e) {
        if (e?.response && e.response.status < 502) { toast.error(errMsg(e)); throw e; }
        // A long reading outlived the proxy – the server finishes it; the posts appear here when they are in.
        toast("Lesingen tar litt tid – postene kommer her så snart de er klare");
        for (let i = 0; i < 40; i += 1) {
          await new Promise((res) => { setTimeout(res, 3000); });
          const r = await api.get(`/projects/${id}`).catch(() => null);
          if (r && count(r.data.context) > before) { apply(r); toast.success(`${count(r.data.context) - before} arbeidsposter lest`); return r.data; }
        }
        toast.error(errMsg(e));
        throw e;
      }
    },
    applyTemplate: (template, keys) => run(api.post(`/projects/${id}/mal`, { template, ...(keys ? { keys } : {}) })).then((d) => { toast.success(d?.added ? `${d.added} standardposter lagt inn` : "Ingen nye poster – de finnes allerede"); return d; }),
    applyOwnTemplate: (tid, into) => run(api.post(`/projects/${id}/maler/${tid}`, { into: into || null })).then((d) => { toast.success(into ? `${d?.added} underjobber lagt inn fra malen` : `${d?.added} ${d?.added === 1 ? "jobb" : "jobber"} lagt inn fra malen`); return d; }),
    setSettingsVersion: (version) => run(api.post(`/projects/${id}/settings-version`, { version }), `Saken bruker nå satsversjon v${version}`),
    undo: (changeId) => run(api.post(`/projects/${id}/undo`, { change_id: changeId || null })).then((d) => { toast.success(`Angret: ${d.undone?.label || "siste endring"}`); return d; }),
    /** The in-app document viewer: the original opens beside the work, at the exact page / cell. Nothing leaves the workspace. */
    openDoc: (docId, where) => setViewer({ docId, where: where || null }),
    /** Documents onto the case from wherever the work is – the same path as the «+» in the assistant (store, read, structure). */
    upload: (files) => upload(Array.isArray(files) ? files : Array.from(files || [])),
    /** Ask the assistant from the work itself – opens the conversation and sends the question. The answer is a proposal, never an applied change. */
    ask: (content) => { setChatHidden(false); if (!isLg) setMobileView("chat"); return send(content); },
  }), [id, run, act, threadOps, apply, send, setChatHidden, isLg, setMobileView, upload]);

  const patch = useCallback((body, msg) => ops.patchCase(body, msg).catch(() => {}), [ops]);

  const pin = useCallback(async () => {
    try {
      const r = await api.post(`/projects/${id}/pin`);
      setProject((p) => (p ? { ...p, pinned_by: r.data.pinned ? [...(p.pinned_by || []), user.id] : (p.pinned_by || []).filter((u) => u !== user.id) } : p));
      toast.success(r.data.pinned ? "Festet øverst" : "Løsnet");
      window.dispatchEvent(new Event("projects:changed"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [id, user]);

  const remove = useCallback(async () => {
    try {
      await api.delete(`/projects/${id}`);
      window.dispatchEvent(new Event("projects:changed"));
      toast.success("Saken er slettet");
      navigate("/");
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [id, navigate]);

  /** Utkast → aktiv sak. Explicit, idempotent on the server; CRM is touched only now. */
  const confirmCase = useCallback(async (body) => {
    try {
      const r = await api.post(`/projects/${id}/confirm`, body);
      apply(r);
      toast.success(r.data.already ? "Arbeidet var allerede opprettet" : `Arbeidet er opprettet – sak ${r.data.project.number}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [id, apply]);
  const restoreCase = useCallback(async () => {
    try {
      apply(await api.post(`/projects/${id}/restore`));
      toast.success("Arbeidet er gjenopprettet");
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [id, apply]);
  const discardCase = useCallback(async () => {
    try {
      apply(await api.post(`/projects/${id}/discard`));
      toast("Forkastet – ingenting er slettet", { action: { label: "Angre", onClick: restoreCase } });
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [id, apply, restoreCase]);

  const changeTab = (t) => {
    setTab(t);
    api.patch(`/projects/${id}`, { last_tab: t }).catch(() => {});
  };
  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === "j") { e.preventDefault(); toggleChat(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleChat]);
  const msgCount = project?.messages?.length || 0;
  const prevCount = useRef(msgCount);
  useEffect(() => {
    // A reply that arrives while the assistant is hidden shows as a quiet count – the panel never opens by itself.
    if (msgCount > prevCount.current && isLg && chatHidden && project?.messages?.[msgCount - 1]?.role === "assistant") setUnseen((n) => n + 1);
    prevCount.current = msgCount;
  }, [msgCount, isLg, chatHidden, project?.messages]);
  useEffect(() => { if (!chatHidden) setUnseen(0); }, [chatHidden]);

  // Desktop: the assistant takes over the left context panel (Arbeid comes back when it closes) – never a fifth column.
  const showChat = isLg ? !chatHidden : mobileView === "chat";
  useEffect(() => {
    if (!setLeftActive) return undefined;
    setLeftActive(Boolean(isLg && showChat && !error));
    return () => setLeftActive(false);
  }, [isLg, showChat, setLeftActive, error]);

  // Inspector size: docked when the work keeps MIN_WORK_W beside it. The panel first yields width down to its minimum
  // (a 1366 px laptop still gets list + panel); «utvid» takes most of the surface. Otherwise it overlays – the list is never squeezed.
  const wideW = Math.max(inspWidth, Math.min(Math.round(canvasW * 0.62), canvasW - 420));
  const fitW = Math.min(inspWidth, canvasW - MIN_WORK_W);
  const inspectorDock = sm && (wide ? canvasW >= 420 + wideW : fitW >= PANELS.inspector.min);
  const inspW = wide ? wideW : inspectorDock ? fitW : inspWidth;
  useEffect(() => {
    // Medium laptop: when the inspector needs the room and Egil has not locked the side panel open, the panel steps aside.
    if (!requestAutoHide) return undefined;
    const avail = winW - (railWidth || RAIL_W) - 24;
    if (!isLg || leftLocked || showChat) { requestAutoHide(false); return undefined; }
    if (tab === "meldinger") {
      // Samtaler + samtale need room of their own: on a medium laptop the Arbeid panel steps aside while Meldinger is open.
      requestAutoHide(avail - (leftPanelWidth || PANELS.left.def) < PANELS.threads.def + 760);
      return undefined;
    }
    if (!inspOpen) { requestAutoHide(false); return undefined; }
    const fitsWithLeft = avail - (leftPanelWidth || PANELS.left.def) >= MIN_WORK_W + inspWidth;
    const fitsWithout = avail >= MIN_WORK_W + PANELS.inspector.min;
    requestAutoHide(!fitsWithLeft && fitsWithout);
    return undefined;
  }, [requestAutoHide, isLg, inspOpen, leftLocked, showChat, winW, leftPanelWidth, inspWidth, railWidth, tab]);
  useEffect(() => () => requestAutoHide?.(false), [requestAutoHide]);
  const slot = useMemo(() => ({ el: slotEl, dock: inspectorDock, mobile: !sm, width: inspWidth, dockW: inspOpen && inspectorDock ? inspW : 0, wide, setWide, setOpen: setInspOpen }), [slotEl, inspectorDock, sm, inspWidth, inspOpen, inspW, wide, setWide]);

  if (error) return <CaseError missing={error.missing} msg={error.msg} />;

  const tabProps = { ctx, project, ops, busy, filter, setFilter, focus, setFocus, target, setTarget, panel, setPanel, intent, onIntentDone: () => setIntent(null), reportScreen, draftOps: { confirm: confirmCase, discard: discardCase, restore: restoreCase } };
  const canvas = !ctx ? null : tab === "oversikt" ? <OversiktTab {...tabProps} /> : tab === "krav" ? <KravTab {...tabProps} /> : tab === "kalkyle" ? <KalkyleTab {...tabProps} /> : tab === "meldinger" ? <MeldingerTab {...tabProps} threads={threads} threadId={threadId} setThreadId={setThreadId} canWrite={Boolean(user)} me={user?.id} draft={isDraftCase(project)} compose={compose} onComposeDone={() => setCompose(null)} /> : <TilbudTab {...tabProps} />;
  const showCanvas = isLg || mobileView === "canvas";
  const pinned = Boolean(project && (project.pinned_by || []).includes(user?.id));
  const chat = !project ? <div className="space-y-3 p-6"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-4 w-1/2" /></div> : (
    <ProjectChat messages={project.messages || []} tab={tab} busy={busy || up.busy} onSend={send} onGoto={(t, ref) => { setTab(t); if (ref) setFocus(ref); if (!isLg) setMobileView("canvas"); }} onUpload={upload} onFeedback={feedback} onDecide={decide} undo={ctx?.undo || null} onUndo={ctx?.undo ? () => ops.undo(ctx.undo.id).catch(() => {}) : undefined} className="h-full" uploads={<><ReadingStatus reading={project.reading} onRetry={() => readingCtl("retry")} onCancel={() => readingCtl("cancel")} onDismiss={dismissReading} /><CaseUploads up={up} /></>} placeholder={PLACEHOLDER} context={target} onClearContext={() => setTarget(null)} thinkingLabels={up.busy ? ["Laster opp og leser dokumentene…", "Setter roller og kobler til arbeidspostene…"] : undefined} />
  );
  const viewerDoc = viewer ? (ctx?.documents || []).find((d) => d.id === viewer.docId) : null;

  return (
    <div className="ws flex h-full w-full flex-col overflow-hidden bg-white" data-testid="project-page">
      <header className="relative z-20 shrink-0 border-b border-black/[0.05] bg-white/90 px-2 backdrop-blur-md sm:px-3 lg:grid lg:h-[60px] lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:items-center lg:gap-4 lg:px-5" data-testid="project-header">
        <div className="flex h-12 min-w-0 items-center gap-1.5 lg:h-auto">
          {isLg && panelCollapsed && togglePanel ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" onClick={togglePanel} className="-ml-1 mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-foreground/50 transition-colors hover:bg-black/[0.05] hover:text-foreground" aria-label="Vis sidemenyen" data-testid="project-panel-open"><PanelLeftOpen className="h-[18px] w-[18px]" strokeWidth={1.8} /></button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="flex items-center gap-2 rounded-lg px-2.5 py-1 text-[12px]">Vis sidemenyen <span className="kbd">⌘⇧S</span></TooltipContent>
            </Tooltip>
          ) : null}
          {!isLg ? <button type="button" onClick={() => navigate(-1)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-black/[0.05] hover:text-foreground" aria-label="Tilbake" data-testid="project-back-button"><ArrowLeft className="h-4 w-4" /></button> : null}
          {project ? (
            <div className="min-w-0 leading-tight" data-testid="project-title" title={`${project.name} · #${project.number}${project.customer?.name ? ` · ${project.customer.name}` : ""}${project.vessel?.name ? ` · ${project.vessel.name}` : ""}`}>
              <div className="truncate text-[15.5px] font-semibold tracking-[-0.015em]">{project.name || "Ny sak"}</div>
              <div className="truncate text-[13px] text-muted-foreground">{project.customer?.name ? <>{project.customer.name}<span className="mx-1.5 text-muted-foreground/50">·</span></> : null}<span data-testid="project-status" data-status={project.status}>{phaseLabel(project, ctx)}</span></div>
            </div>
          ) : <Skeleton className="h-4 w-44" />}
          {project && !isLg ? <div className="ml-auto"><ProjectMenu project={project} pinned={pinned} draft={isDraftCase(project)} onRename={(name) => patch({ name }, "Navnet er oppdatert")} onPin={pin} onStatus={(status) => patch({ status }, "Status oppdatert")} onDelete={remove} onActivity={() => setActivityOpen(true)} /></div> : null}
        </div>
        {isLg ? <TabBar tab={tab} setTab={changeTab} mobile={false} mobileView={mobileView} setMobileView={setMobileView} unread={threads?.unread || 0} /> : (
          <div className="flex min-w-0 pb-2">
            <TabBar tab={tab} setTab={changeTab} mobile mobileView={mobileView} setMobileView={setMobileView} unread={threads?.unread || 0} />
          </div>
        )}
        <div className="hidden min-w-0 items-center justify-end gap-0.5 lg:flex" data-testid="project-header-actions">
          {project ? (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" onClick={toggleChat} aria-pressed={!chatHidden} className={cn("mr-1 inline-flex h-8 items-center gap-1.5 rounded-[8px] px-2.5 text-[13.5px] font-medium transition-colors hover:text-foreground", chatHidden ? "text-foreground/60" : "text-foreground")} data-testid="project-toggle-chat" data-open={chatHidden ? "0" : "1"}>
                    <Sparkles className="h-3.5 w-3.5" strokeWidth={2} /> Assistent
                    {chatHidden && unseen ? <span className="ml-0.5 rounded-full bg-primary px-1.5 font-mono text-[10.5px] font-semibold tabular-nums text-white" data-testid="project-chat-unseen">{unseen}</span> : null}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="flex items-center gap-2 rounded-lg px-2.5 py-1 text-[12px]">{chatHidden ? "Åpne assistenten" : "Skjul assistenten"} <span className="kbd">⌘J</span></TooltipContent>
              </Tooltip>
              <ShareButton project={project} ctx={ctx} />
              <ProjectMenu project={project} pinned={pinned} draft={isDraftCase(project)} onRename={(name) => patch({ name }, "Navnet er oppdatert")} onPin={pin} onStatus={(status) => patch({ status }, "Status oppdatert")} onDelete={remove} onActivity={() => setActivityOpen(true)} />
            </>
          ) : null}
        </div>
      </header>

      {isLg && showChat && leftSlot?.el ? createPortal(
        <section className="flex h-full min-h-0 flex-col" data-testid="project-chat-pane" aria-label="Assistent" data-placement="left-panel">
          <AssistantHead onClose={() => setChatHidden(true)} />
          <div className="min-h-0 flex-1">{chat}</div>
        </section>,
        leftSlot.el,
      ) : null}

      <div className="relative grid min-h-0 flex-1 grid-cols-1" data-testid="project-split" data-chat={isLg ? (showChat ? "left-panel" : "hidden") : showChat ? "mobile" : "hidden"}>
        {!isLg && showChat ? <section className="flex min-h-0 min-w-0 flex-col" data-testid="project-chat-pane" aria-label="Assistent" data-placement="mobile">{chat}</section> : null}
        {showCanvas ? (
          <section ref={canvasRef} className="relative flex min-h-0 min-w-0 flex-col" data-testid="project-canvas" aria-label="Arbeidsdokument" data-inspector={inspOpen ? (inspectorDock ? "dock" : "overlay") : "closed"} data-inspector-width={inspOpen && inspectorDock ? inspW : undefined} onDragEnter={onCanvasDragEnter} onDragOver={onCanvasDragOver} onDragLeave={onCanvasDragLeave} onDrop={onCanvasDrop} data-dropping={dropping ? "1" : "0"}>
            {dropping ? (
              <div className="pointer-events-none absolute inset-3 z-30 flex items-center justify-center rounded-[22px] border-2 border-dashed border-primary/60 bg-white/85 backdrop-blur-sm" data-testid="canvas-drop-overlay">
                <div className="text-center"><div className="text-[16px] font-semibold text-foreground">Slipp for å legge dokumentene på saken</div><p className="mt-1 text-[13.5px] text-muted-foreground">Leses, får rolle og kobles til arbeidspostene. En ny versjon av spesifikasjonen blir lagt fram for gjennomgang – ikke tatt inn i stillhet.</p></div>
              </div>
            ) : null}
            <InspectorSlot.Provider value={slot}>
              <div className="flex min-h-0 flex-1 overflow-hidden bg-white">
                <div className={cn("min-h-0 min-w-0 flex-1 scrollbar-thin", tab === "meldinger" ? "flex flex-col overflow-hidden" : "overflow-y-auto")} data-testid="project-canvas-scroll">
                  {!ctx ? <div className="space-y-4 p-8"><Skeleton className="h-5 w-1/3" /><Skeleton className="h-4 w-2/3" /><Skeleton className="h-24" /><Skeleton className="h-24" /></div> : (
                    <div key={tab} className={cn("sheet-enter mx-auto w-full", CANVAS_W[tab], tab === "meldinger" && "min-h-0 flex-1")}>{canvas}</div>
                  )}
                </div>
                <div className={cn("relative shrink-0 border-l border-black/[0.07] bg-white", !(inspOpen && inspectorDock) && "hidden")} style={{ width: inspW }} data-testid="canvas-inspector-wrap">
                  {!wide ? <ResizeHandle edge="left" rtl value={inspW} min={PANELS.inspector.min} max={PANELS.inspector.max} onResize={resizeInsp} onReset={resetInsp} label="Bredde på detaljpanelet" testId="inspector-handle" /> : null}
                  <div ref={setSlotEl} className="flex h-full min-h-0 flex-col" data-testid="canvas-inspector-slot" />
                </div>
              </div>
            </InspectorSlot.Provider>
          </section>
        ) : null}
      </div>
      {project && !isDraftCase(project) ? <RisikoSheet project={project} onContext={setCtx} /> : null}
      {project && !isDraftCase(project) ? <KapasitetSheet project={project} onContext={setCtx} /> : null}
      {project ? <ActivityPanel open={activityOpen} onOpenChange={setActivityOpen} projectId={id} onGoto={(t, ref) => { setTab(t); if (ref) setFocus(ref); if (!isLg) setMobileView("canvas"); }} /> : null}
      <SplitDialog ctx={ctx} projectId={id} ops={ops} />
      <MengderSheet projectId={id} ops={ops} />
      {viewer ? <DocViewer caseId={id} doc={viewerDoc} docId={viewer.docId} where={viewer.where} docs={(ctx?.documents || []).filter((d) => d.status !== "duplikat")} onPick={(docId) => setViewer({ docId, where: null })} onClose={() => setViewer(null)} mobile={!sm} /> : null}
    </div>
  );
}
